import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  ClearVibeAppIconPathVarsSchema,
  ClearVibeAppIconResponseSchema,
  CreateVibeAppBodySchema,
  CreateVibeAppResponseSchema,
  DeleteVibeAppPathVarsSchema,
  DeleteVibeAppResponseSchema,
  FetchVibeAppIconPathVarsSchema,
  FetchVibeAppIconResponseSchema,
  GetVibeAppPathVarsSchema,
  GetVibeAppResponseSchema,
  ListVibeAppsResponseSchema,
  RollbackVibeAppBodySchema,
  RollbackVibeAppPathVarsSchema,
  RollbackVibeAppResponseSchema,
  SetVibeAppIconBodySchema,
  SetVibeAppIconPathVarsSchema,
  SetVibeAppIconResponseSchema,
  UpdateVibeAppBodySchema,
  UpdateVibeAppPathVarsSchema,
  UpdateVibeAppResponseSchema,
  VibeRegisterAsToolBodySchema,
  VibeRegisterAsToolPathVarsSchema,
  VibeRegisterAsToolResponseSchema
} from "../schemas";

export const VibeAppEndpoints = {
  // ---------------------------------------------------------------------------
  // VibeApp CRUD
  // ---------------------------------------------------------------------------

  ListApps: {
    method: "GET" as const,
    path: "/api/vibe/apps",
    Response: ApiSuccessSchema(ListVibeAppsResponseSchema)
  },

  CreateApp: {
    method: "POST" as const,
    path: "/api/vibe/apps",
    Body: CreateVibeAppBodySchema,
    Response: ApiSuccessSchema(CreateVibeAppResponseSchema)
  },

  GetApp: {
    method: "GET" as const,
    path: "/api/vibe/apps/:id",
    PathVars: GetVibeAppPathVarsSchema,
    Response: ApiSuccessSchema(GetVibeAppResponseSchema)
  },

  /**
   * Partial update on an app. Every body field is optional — only the
   * fields the caller wants to change need to be sent. Empty bodies
   * are rejected so an accidental `{}` doesn't silently no-op a
   * client bug.
   */
  UpdateApp: {
    method: "PATCH" as const,
    path: "/api/vibe/apps/:id",
    PathVars: UpdateVibeAppPathVarsSchema,
    Body: UpdateVibeAppBodySchema,
    Response: ApiSuccessSchema(UpdateVibeAppResponseSchema)
  },

  /**
   * Roll an app back onto a previous version. Supersedes the currently-
   * serving HEALTHY deployment and re-activates a SUPERSEDED predecessor
   * (SUPERSEDED → DEPLOYING) in one atomic transaction; the agent re-places
   * the target's retained image, keeping the current version serving until
   * the restored one reports healthy (blue-green, no downtime).
   * Non-destructive — it ADDS a rollout, deletes nothing.
   *
   * `Body.targetDeploymentId` names which SUPERSEDED deployment to restore
   * (the console's per-row "Roll back to this"). Omit it to fall back to
   * the pre-NEX-3083 behavior — restore the single most-recent SUPERSEDED
   * predecessor — kept for any caller that predates the console change.
   *
   * 404 if the app (or a named target) is not in the caller's org. 409 when
   * there is nothing to roll back to: no live (HEALTHY) version, no
   * previous (SUPERSEDED) version, the named target is already the one
   * serving, or a deploy is already in flight.
   */
  RollbackApp: {
    method: "POST" as const,
    path: "/api/vibe/apps/:id/rollback",
    PathVars: RollbackVibeAppPathVarsSchema,
    Body: RollbackVibeAppBodySchema,
    Response: ApiSuccessSchema(RollbackVibeAppResponseSchema)
  },

  /**
   * Soft-delete an app. Stamps `deletedAt`, so the app immediately drops
   * out of every read and the agent desired-state projection (all exclude
   * soft-deleted rows) — its running deployment is torn down on the agent's
   * next pass. The row + its deployment / audit history stay for
   * recoverability; archiving the git repository is a separate follow-up.
   *
   * 404 if the app is not in the caller's org OR is already deleted (a miss
   * is indistinguishable — no leaked deletion signal).
   */
  DeleteApp: {
    method: "DELETE" as const,
    path: "/api/vibe/apps/:id",
    PathVars: DeleteVibeAppPathVarsSchema,
    Response: ApiSuccessSchema(DeleteVibeAppResponseSchema)
  },

  /**
   * Replace the app's icon with an uploaded image.
   *
   * `PUT` rather than `POST`: the app has exactly one icon, the body fully
   * describes it, and sending the same body twice must leave the same state —
   * which is idempotence, not creation. Overwrites whatever was there,
   * including a previously fetched favicon, because a human choosing an image
   * is the strongest statement anyone makes about how the app should look.
   *
   * The stored image is normalised server-side (square PNG), so what comes back
   * is not the bytes that were sent. 400 on anything that does not decode as an
   * image or exceeds the size ceiling; 404 if the app is not in the caller's org.
   */
  SetAppIcon: {
    method: "PUT" as const,
    path: "/api/vibe/apps/:id/icon",
    PathVars: SetVibeAppIconPathVarsSchema,
    Body: SetVibeAppIconBodySchema,
    Response: ApiSuccessSchema(SetVibeAppIconResponseSchema)
  },

  /**
   * Adopt the app's own favicon as its icon.
   *
   * Fetches the app's homepage over its canonical public URL, reads the
   * `<link rel="icon">` it declares (falling back to `/favicon.ico`), and
   * stores what comes back. `POST` because it is an action with a side effect
   * on the wider world — it makes an outbound request — and is emphatically not
   * idempotent in cost.
   *
   * 409 when the app has no public URL yet (never deployed), when the site
   * declares no usable icon, or when the fetch fails. Those are all "we tried
   * and there is nothing to adopt", which is a conflict with the caller's
   * expectation rather than a server fault — the existing icon is left alone in
   * every one of them.
   */
  FetchAppIcon: {
    method: "POST" as const,
    path: "/api/vibe/apps/:id/icon/fetch",
    PathVars: FetchVibeAppIconPathVarsSchema,
    Response: ApiSuccessSchema(FetchVibeAppIconResponseSchema)
  },

  /**
   * Drop the app's icon and fall back to the generated monogram.
   *
   * Idempotent: clearing an app that has no icon succeeds and changes nothing,
   * so a double-click cannot 404 on its second half.
   */
  ClearAppIcon: {
    method: "DELETE" as const,
    path: "/api/vibe/apps/:id/icon",
    PathVars: ClearVibeAppIconPathVarsSchema,
    Response: ApiSuccessSchema(ClearVibeAppIconResponseSchema)
  },

  /**
   * Register a deployed app as a CUSTOM_MANIFEST agent tool — the session
   * console path (the same bridge as the public-v1 api-key endpoint). The
   * tool's endpoint is the app's server-owned canonical URL; per-app edge auth
   * is injected server-side, so the console sends only the OpenAPI spec (plus
   * an optional name / description override).
   *
   * 404 if the app is not in the caller's org. 409 when the app is already
   * registered as a tool or has no healthy deployment to register. 400 on a
   * malformed OpenAPI spec.
   */
  RegisterAsTool: {
    method: "POST" as const,
    path: "/api/vibe/apps/:id/register-as-tool",
    PathVars: VibeRegisterAsToolPathVarsSchema,
    Body: VibeRegisterAsToolBodySchema,
    Response: ApiSuccessSchema(VibeRegisterAsToolResponseSchema)
  }
} as const;
